// frontend/src/services/api.js
const backendUrl = process.env.REACT_APP_BACKEND_URL;

// Hilfsfunktion zum Abrufen des Tokens und Erstellen der Header
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  if (!token) {
    console.error('API Service: Kein Authentifizierungstoken gefunden.');
    // Werfe einen Fehler, der von handleApiError erkannt wird
    const error = new Error('Nicht authentifiziert');
    error.status = 401;
    throw error;
  }
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json', // Standard für POST/PUT
  };
};

// Hilfsfunktion zur Verarbeitung der Fetch-Antwort
const handleResponse = async (response) => {
  if (!response.ok) {
    // Spezifische Behandlung für 401/403, um Logout auszulösen
    if (response.status === 401 || response.status === 403) {
      const error = new Error(`Authentifizierungsfehler: ${response.status}`);
      error.status = response.status;
      throw error;
    }
    // Versuche, Fehlerdetails aus dem Body zu lesen
    let errorData;
    try { errorData = await response.json(); }
    catch (e) { errorData = { message: `HTTP-Fehler: ${response.status}` }; }
    const error = new Error(errorData.message || errorData.error || `HTTP-Fehler: ${response.status}`);
    error.status = response.status;
    throw error;
  }
  // Behandle 204 No Content (z.B. bei DELETE)
  if (response.status === 204) {
    return null; // Oder { success: true } je nach Bedarf
  }
  // Versuche, JSON zu parsen
  try {
      return await response.json();
  } catch (e) {
      console.error("Fehler beim Parsen der JSON-Antwort trotz Status OK:", e);
      throw new Error("Ungültige JSON-Antwort vom Server erhalten.");
  }
};


// --- API Funktionen ---

// User
export const fetchUserProfileAPI = async () => {
  const headers = getAuthHeaders();
  const response = await fetch(`${backendUrl}/api/user/profile`, { headers });
  return handleResponse(response);
};

// Diese Funktion speichert alle Benutzerpräferenzen (Spalten, Ansicht, etc.)
export const updateUserProfileAPI = async (profileData) => {
  const headers = getAuthHeaders();
  const response = await fetch(`${backendUrl}/api/user/profile`, {
    method: 'PUT',
    headers: headers,
    body: JSON.stringify(profileData),
  });
  return handleResponse(response);
};


// Books
// *** KORRIGIERTE SIGNATUR UND VERWENDUNG ***
export const fetchBooksAPI = async (params) => { // Akzeptiert das URLSearchParams-Objekt direkt
  const headers = getAuthHeaders();

  // Loggen, was hier ankommt und gesendet wird (sehr wichtig!)
  console.log('>>> api.js: fetchBooksAPI received & sending params:', params.toString());

  // Verwende das übergebene params-Objekt direkt
  const response = await fetch(`${backendUrl}/api/books?${params.toString()}`, { headers });
  return handleResponse(response);
};

export const fetchBookAPI = async (id) => {
  const headers = getAuthHeaders();
  const response = await fetch(`${backendUrl}/api/books/${id}`, { headers });
  return handleResponse(response);
};

export const addBookAPI = async ({ isbn, location }) => {
  const headers = getAuthHeaders();
  const response = await fetch(`${backendUrl}/api/books`, {
    method: 'POST', headers, body: JSON.stringify({ isbn, location }),
  });
  // Spezifische Fehlerbehandlung für 409 direkt hier
  if (response.status === 409) {
      const errorData = await response.json();
      const error = new Error(errorData.error || 'Buch mit dieser ISBN existiert bereits.');
      error.status = 409; throw error;
  }
  return handleResponse(response); // Allgemeine Behandlung für andere Fehler
};

export const updateBookAPI = async (id, bookData) => {
  const headers = getAuthHeaders();
  const response = await fetch(`${backendUrl}/api/books/${id}`, {
    method: 'PUT', headers, body: JSON.stringify(bookData),
  });
  return handleResponse(response);
};

export const deleteBookAPI = async (id) => {
  const headers = getAuthHeaders();
  // Für DELETE ist oft nur der Auth-Header nötig, kein Content-Type
  const authHeaderOnly = { Authorization: headers.Authorization };
  const response = await fetch(`${backendUrl}/api/books/${id}`, {
    method: 'DELETE', headers: authHeaderOnly,
  });
  // Gibt null zurück bei Erfolg (204 No Content)
  return handleResponse(response);
};

export const massImportBookAPI = async ({ isbn, location }) => {
    const headers = getAuthHeaders();
    const response = await fetch(`${backendUrl}/api/books/massimport`, {
        method: 'POST', headers, body: JSON.stringify({ isbn, location }),
    });
    // Spezifische Fehlerbehandlung für 409
    if (response.status === 409) {
        const errorData = await response.json();
        const error = new Error(errorData.error || `Buch mit ISBN ${isbn} existiert bereits.`);
        error.status = 409; error.isbn = isbn; throw error;
    }
    return handleResponse(response);
};

// --- NEUE API Funktionen für Ratings ---

/**
 * Holt die Bewertung eines bestimmten Buches für den eingeloggten User.
 * @param {string} bookId - Die ID des Buches.
 * @returns {Promise<object | null>} Das gefundene Rating-Objekt oder null.
 */
export const fetchRatingForBookAPI = async (bookId) => {
  const headers = getAuthHeaders();
  const response = await fetch(`${backendUrl}/api/ratings/${bookId}`, { headers });
  // handleResponse gibt bei 200 OK mit null Body auch null zurück
  return handleResponse(response);
};

/**
 * Holt eine Liste von Büchern, die der User noch nicht bewertet hat.
 * @param {number} [limit=20] - Optionale Begrenzung der Anzahl der Bücher.
 * @returns {Promise<Array>} Ein Array von Buch-Objekten ({ _id, title, authors, coverUrl, cachedCoverUrl, thumbnail }).
 */
export const fetchUnratedBooksAPI = async (limit = 20) => {
  const headers = getAuthHeaders();
  const params = new URLSearchParams({ limit: limit.toString() });
  const response = await fetch(`${backendUrl}/api/books/unrated?${params.toString()}`, { headers });
  return handleResponse(response);
};

/**
 * Sendet die Bewertung eines Buches an das Backend.
 * @param {string} bookId - Die ID des zu bewertenden Buches.
 * @param {'read' | 'wants_to_read'} readingStatus - Der Lesestatus.
 * @param {'keep' | 'discard'} keepStatus - Der Behalten-Status.
 * @returns {Promise<object>} Das erstellte/aktualisierte Rating-Objekt vom Backend.
 */
export const rateBookAPI = async (bookId, readingStatus, keepStatus) => {
  const headers = getAuthHeaders();
  const body = JSON.stringify({ bookId, readingStatus, keepStatus });
  const response = await fetch(`${backendUrl}/api/ratings`, {
    method: 'POST',
    headers: headers,
    body: body,
  });
  // Spezifische Fehlerbehandlung für 409 (obwohl upsert das meist fängt)
  if (response.status === 409) {
      const errorData = await response.json();
      const error = new Error(errorData.message || 'Bewertung für dieses Buch existiert bereits (Konflikt).');
      error.status = 409; throw error;
  }
  // Allgemeine Fehlerbehandlung
  return handleResponse(response);
};

/**
 * Löscht die Bewertung eines Buches (für Undo).
 * @param {string} bookId - Die ID des Buches, dessen Bewertung gelöscht werden soll.
 * @returns {Promise<null>} Gibt null zurück bei Erfolg (204 No Content).
 */
export const deleteRatingAPI = async (bookId) => {
  const headers = getAuthHeaders();
  // Für DELETE ist oft nur der Auth-Header nötig, kein Content-Type
  const authHeaderOnly = { Authorization: headers.Authorization };
  const response = await fetch(`${backendUrl}/api/ratings/${bookId}`, {
    method: 'DELETE',
    headers: authHeaderOnly,
  });
  // handleResponse gibt bei 204 null zurück
  return handleResponse(response);
};

/**
 * Löscht alle Bewertungen (readingStatus, keepStatus) für den eingeloggten User.
 * @returns {Promise<object>} Gibt eine Erfolgsmeldung zurück.
 */
export const resetAllRatingsAPI = async () => {
  const headers = getAuthHeaders();
  // Für DELETE ist oft nur der Auth-Header nötig, kein Content-Type
  const authHeaderOnly = { Authorization: headers.Authorization };
  const response = await fetch(`${backendUrl}/api/ratings/reset-all`, {
    method: 'DELETE',
    headers: authHeaderOnly,
  });
  // handleResponse gibt bei Erfolg das JSON-Objekt mit der Nachricht zurück
  return handleResponse(response);
};


// --- Ende NEUE API Funktionen ---

// --- Ausleihsystem ---
export const borrowBookAPI = async (bookId, borrowerEmail = null) => {
  const headers = getAuthHeaders();
  const body = borrowerEmail ? JSON.stringify({ bookId, borrowerEmail }) : JSON.stringify({ bookId });
  const response = await fetch(`${backendUrl}/api/loans/borrow`, {
    method: 'POST', headers, body
  });
  return handleResponse(response);
};

export const returnBookAPI = async (bookId) => {
  const headers = getAuthHeaders();
  const response = await fetch(`${backendUrl}/api/loans/return`, {
    method: 'POST', headers, body: JSON.stringify({ bookId })
  });
  return handleResponse(response);
};

export const fetchCurrentLoansAPI = async () => {
  const headers = getAuthHeaders();
  const response = await fetch(`${backendUrl}/api/loans/current`, { headers });
  return handleResponse(response);
};

export const fetchLoanHistoryAPI = async () => {
  const headers = getAuthHeaders();
  const response = await fetch(`${backendUrl}/api/loans/history`, { headers });
  return handleResponse(response);
};

// --- Aktivitätslog ---
export const fetchActivityLogAPI = async (params = {}) => {
  const headers = getAuthHeaders();
  const url = new URL(`${backendUrl}/api/activitylog`);
  Object.entries(params).forEach(([key, value]) => url.searchParams.append(key, value));
  const response = await fetch(url, { headers });
  return handleResponse(response);
};

// Locations
export const fetchLocationsAPI = async () => {
  const headers = getAuthHeaders();
  const response = await fetch(`${backendUrl}/api/locations`, { headers });
  return handleResponse(response);
};

export const addLocationAPI = async (locationData) => {
  const headers = getAuthHeaders();
  const response = await fetch(`${backendUrl}/api/locations`, {
    method: 'POST', headers, body: JSON.stringify(locationData),
  });
  return handleResponse(response);
};

export const updateLocationAPI = async (id, locationData) => {
  const headers = getAuthHeaders();
  const response = await fetch(`${backendUrl}/api/locations/${id}`, {
    method: 'PUT', headers, body: JSON.stringify(locationData),
  });
  return handleResponse(response);
};

export const deleteLocationAPI = async (id) => {
  const headers = getAuthHeaders();
  const authHeaderOnly = { Authorization: headers.Authorization };
  const response = await fetch(`${backendUrl}/api/locations/${id}`, {
    method: 'DELETE', headers: authHeaderOnly,
  });
  // Gibt null zurück bei Erfolg (204 No Content)
  return handleResponse(response);
};

// Exportiert ISBNs als CSV für einen Lagerort
export const exportIsbnsCsvAPI = async (locationId) => {
  const headers = getAuthHeaders();
  // Hole als Blob, damit wir die Datei herunterladen können
  const response = await fetch(`${backendUrl}/api/books/export/csv?locationId=${locationId}`, {
    headers,
  });
  if (!response.ok) {
    let errorText = 'Fehler beim CSV-Export';
    try { errorText = (await response.json()).error || errorText; } catch {}
    throw new Error(errorText);
  }
  return response.blob(); // CSV als Blob zurückgeben
};

// Stats
export const fetchStatsAPI = async (endpoint) => {
    const headers = getAuthHeaders();
    const response = await fetch(`${backendUrl}/api/stats/${endpoint}`, { headers });
    return handleResponse(response);
};
